import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Drawer,
  Fab,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import InsightsIcon from "@mui/icons-material/Insights";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import BarChartIcon from "@mui/icons-material/BarChart";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { alpha, keyframes, useTheme, type Theme } from "@mui/material/styles";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";

import { postMultiChatbotStream } from "../../services/aiChatbotApi";
import {
  listConversations,
  createConversation,
  getConversation,
  deleteConversation,
  renameConversation,
  appendMessages as apiAppendMessages,
} from "../../services/chatHistoryApi";
import type { ChatTurn, ConversationSummary, VizIntent } from "../../types/multiChatbot";
import { AssistantMarkdown } from "../common/AssistantMarkdown";
import { MultiChatbotVisualizations } from "./MultiChatbotVisualizations";
import { SEARCE_COLORS } from "../../theme";

const STORAGE_KEY = "bd-portfolio-multi-chatbot-v1";
const CHAT_HISTORY_MESSAGE_LIMIT = 8;
const ASSISTANT_HISTORY_MAX_CHARS = 2000;
const STANDALONE_CHAT_MAX_WIDTH = 780;
const CHARTS_PANEL_WIDTH_STORAGE_KEY = "bd-portfolio-chat-charts-panel-width-v1";

const STANDALONE_MARKDOWN_SX = {
  fontSize: "0.9375rem",
  lineHeight: 1.6,
  "& h1": { fontSize: "1.25rem" },
  "& h2": { fontSize: "1.125rem" },
  "& h3, & h4, & h5, & h6": { fontSize: "1rem" },
  "& code": { fontSize: "0.8125rem" },
  "& pre code": { fontSize: "0.75rem" },
  "& table": { fontSize: "0.8125rem" },
} as const;

const STANDALONE_UI_SX = {} as const;
const CHARTS_PANEL_DEFAULT_WIDTH = 360;
const CHARTS_PANEL_MIN_WIDTH = 280;
const CHARTS_PANEL_MAX_WIDTH = 960;

function readChartsPanelWidth(): number {
  if (typeof localStorage === "undefined") return CHARTS_PANEL_DEFAULT_WIDTH;
  try {
    const v = parseInt(localStorage.getItem(CHARTS_PANEL_WIDTH_STORAGE_KEY) ?? "", 10);
    if (Number.isFinite(v) && v >= CHARTS_PANEL_MIN_WIDTH && v <= CHARTS_PANEL_MAX_WIDTH) return v;
  } catch {
    /* ignore */
  }
  return CHARTS_PANEL_DEFAULT_WIDTH;
}

function clampChartsPanelWidth(width: number, containerWidth?: number): number {
  const maxFromContainer =
    containerWidth != null && containerWidth > 0
      ? Math.max(CHARTS_PANEL_MIN_WIDTH, Math.floor(containerWidth * 0.72))
      : CHARTS_PANEL_MAX_WIDTH;
  const maxW = Math.min(CHARTS_PANEL_MAX_WIDTH, maxFromContainer);
  return Math.min(maxW, Math.max(CHARTS_PANEL_MIN_WIDTH, width));
}

const AI_CONFIGURED = Boolean(process.env.REACT_APP_AI_CHATBOT_URL?.trim());

const streamCursorBlink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

const dotBounce = keyframes`
  0%, 80%, 100% { opacity: 0.3; transform: translateY(0); }
  40% { opacity: 1; transform: translateY(-3px); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const STANDALONE_STARTER_PROMPTS = [
  "Which BU was under-allocated last month?",
  "Which projects are are about to be overdue?",
  "What do our top 10 highest-revenue projects have in common?",
  "Which employees are almost Tier 1 in DRI?",
];

const IDLE_PHRASES = [
  "Reviewing your portfolio context…",
  "Preparing data-backed insights…",
  "Connecting to the latest project signals…",
];

/** Transient UI while SSE stream is active (mirrors test-frontend ChatPanel). */
type StreamProgressState = {
  statusText: string;
  hypotheses: string[];
  toolLabel: string | null;
  provisionalAnswer: string;
};

function newId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function truncateForHistory(text: string, maxLen: number): string {
  const t = text.trim();
  if (t.length <= maxLen) return t;
  return `${t.slice(0, maxLen)}…`;
}

export interface PortfolioChatMessage {
  id: string;
  role: "user" | "assistant";
  text: string;
  isError?: boolean;
  /** Sent as assistant `content` in `chat_history` on the next request. */
  apiHistoryContent?: string;
  /** Charts/tables returned with this assistant turn. */
  vizIntents?: VizIntent[];
  lastToolSql?: string | null;
  lastToolRowCount?: number | null;
  /** Unix ms timestamp set at message creation — optional for backwards compat with stored sessions. */
  timestamp?: number;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function isValidVizIntent(v: unknown): v is VizIntent {
  if (!isRecord(v)) return false;
  const kind = v.kind;
  if (kind !== "table" && kind !== "bar" && kind !== "line" && kind !== "pie") return false;
  if (typeof v.title !== "string") return false;
  return true;
}

function parseVizIntentsFromRaw(raw: unknown): VizIntent[] {
  if (!Array.isArray(raw)) return [];
  const out: VizIntent[] = [];
  for (const v of raw) {
    if (isValidVizIntent(v)) out.push(v);
  }
  return out;
}

function migrateLegacyVizHistory(messages: PortfolioChatMessage[], vizHistory: VizIntent[]): PortfolioChatMessage[] {
  if (vizHistory.length === 0) return messages;
  if (messages.some((m) => (m.vizIntents?.length ?? 0) > 0)) return messages;
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m.role === "assistant" && !m.isError) {
      const copy = [...messages];
      copy[i] = { ...m, vizIntents: vizHistory };
      return copy;
    }
  }
  return messages;
}

function parseStoredSession(raw: string | null): PortfolioChatMessage[] {
  if (!raw) return [];
  try {
    const data = JSON.parse(raw) as unknown;
    if (!isRecord(data)) return [];
    const messagesRaw = data.messages;
    const messages: PortfolioChatMessage[] = [];
    if (Array.isArray(messagesRaw)) {
      for (const item of messagesRaw) {
        if (!isRecord(item)) continue;
        const id = item.id;
        const role = item.role;
        const text = item.text;
        if (typeof id !== "string" || typeof text !== "string") continue;
        if (role !== "user" && role !== "assistant") continue;
        const vizIntents = parseVizIntentsFromRaw(item.vizIntents);
        const lastToolSql =
          typeof item.lastToolSql === "string"
            ? item.lastToolSql
            : item.lastToolSql === null
              ? null
              : undefined;
        const lastToolRowCount =
          typeof item.lastToolRowCount === "number"
            ? item.lastToolRowCount
            : item.lastToolRowCount === null
              ? null
              : undefined;
        messages.push({
          id,
          role,
          text,
          isError: typeof item.isError === "boolean" ? item.isError : undefined,
          apiHistoryContent: typeof item.apiHistoryContent === "string" ? item.apiHistoryContent : undefined,
          vizIntents: vizIntents.length > 0 ? vizIntents : undefined,
          lastToolSql,
          lastToolRowCount,
        });
      }
    }
    const legacyViz = parseVizIntentsFromRaw(data.vizHistory);
    return migrateLegacyVizHistory(messages, legacyViz);
  } catch {
    return [];
  }
}

function findPrecedingUserQuestion(messages: PortfolioChatMessage[], assistantMessageId: string): string | null {
  const idx = messages.findIndex((m) => m.id === assistantMessageId);
  if (idx <= 0) return null;
  for (let i = idx - 1; i >= 0; i--) {
    if (messages[i].role === "user") {
      const t = messages[i].text.trim();
      return t.length > 120 ? `${t.slice(0, 120)}…` : t;
    }
  }
  return null;
}

function priorMessagesToChatHistory(messages: PortfolioChatMessage[]): ChatTurn[] {
  const filtered = messages.filter((m) => !m.isError).slice(-CHAT_HISTORY_MESSAGE_LIMIT);

  // Trim trailing user turns — no paired assistant reply yet (reload or prior failure).
  while (filtered.length > 0 && filtered[filtered.length - 1].role === "user") {
    filtered.pop();
  }

  // Collapse consecutive same-role messages left behind by filtered-out error messages.
  // When two user turns are adjacent, keep the later (more recent) one.
  const alternating: typeof filtered = [];
  for (const m of filtered) {
    if (alternating.length > 0 && alternating[alternating.length - 1].role === m.role) {
      alternating[alternating.length - 1] = m;
    } else {
      alternating.push(m);
    }
  }

  return alternating.map((m) => ({
    role: m.role,
    content:
      m.role === "assistant"
        ? (m.apiHistoryContent ?? truncateForHistory(m.text, ASSISTANT_HISTORY_MAX_CHARS))
        : m.text.trim(),
  }));
}

function errorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const status = err.response?.status;
    if (status === 400 || status === 422) {
      const d = err.response?.data as { detail?: unknown } | undefined;
      if (d?.detail != null) {
        return typeof d.detail === "string" ? d.detail : JSON.stringify(d.detail);
      }
    }
    const d = err.response?.data as { detail?: string; message?: string } | undefined;
    if (d?.detail) return String(d.detail);
    if (d?.message) return String(d.message);
    if (err.message) return err.message;
  }
  if (err instanceof Error) return err.message;
  return "Something went wrong. Please try again.";
}

function ChartOpenBox({
  count,
  isActive,
  onClick,
  standalone,
}: {
  count: number;
  isActive: boolean;
  onClick: () => void;
  standalone: boolean;
}): React.ReactElement {
  return (
    <Paper
      component="button"
      type="button"
      onClick={onClick}
      variant="outlined"
      aria-expanded={isActive}
      sx={{
        mt: 1,
        px: 1.5,
        py: 0.75,
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 1,
        cursor: "pointer",
        textAlign: "left",
        borderColor: isActive ? "primary.main" : "divider",
        bgcolor: (t) => (isActive ? alpha(t.palette.primary.main, 0.08) : t.palette.background.paper),
        "&:hover": {
          borderColor: "primary.light",
          bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
        },
      }}
    >
      <BarChartIcon fontSize="small" color={isActive ? "primary" : "action"} />
      <Typography variant={standalone ? "body2" : "caption"} fontWeight={600} color={isActive ? "primary.main" : "text.primary"}>
        {isActive ? "Hide charts" : `View charts (${count})`}
      </Typography>
    </Paper>
  );
}

function TurnChartsBody({
  activeMessage,
  userQuestionPreview,
}: {
  activeMessage: PortfolioChatMessage | null;
  userQuestionPreview: string | null;
}): React.ReactElement {
  const viz = activeMessage?.vizIntents ?? [];
  const sql = activeMessage?.lastToolSql;
  const hasSql = typeof sql === "string" && sql.trim() !== "";
  const rowCount = activeMessage?.lastToolRowCount;

  if (!activeMessage || viz.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Open a chart box under an assistant answer to view its visualizations here.
      </Typography>
    );
  }

  return (
    <>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
        Charts for this answer
      </Typography>
      {userQuestionPreview ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5, fontStyle: "italic" }}>
          &ldquo;{userQuestionPreview}&rdquo;
        </Typography>
      ) : null}
      <MultiChatbotVisualizations items={viz} />
      <Accordion sx={{ mt: 2 }} disableGutters elevation={0} variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="caption" fontWeight={600}>
            Query details — rows: {String(rowCount ?? "—")}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {hasSql ? (
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 1,
                fontSize: "0.7rem",
                overflow: "auto",
                maxHeight: 200,
                bgcolor: "grey.100",
                borderRadius: 1,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {sql}
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary">
              No tool SQL for this reply.
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>
    </>
  );
}

function ThinkingDots(): React.ReactElement {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, py: 0.5 }}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            bgcolor: "primary.main",
            animation: `${dotBounce} 1.1s ease-in-out infinite`,
            animationDelay: `${i * 0.15}s`,
          }}
        />
      ))}
    </Box>
  );
}

function IdleWaitStrip({ phrase }: { phrase: string }): React.ReactElement {
  return (
    <Paper
      variant="outlined"
      sx={{
        alignSelf: "flex-start",
        maxWidth: "100%",
        p: 1.5,
        borderRadius: 2,
        bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
        borderColor: "divider",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
        <ThinkingDots />
        <AnimatePresence mode="wait">
          <motion.div
            key={phrase}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.2 }}
          >
            <Typography variant="body2" color="text.secondary">
              {phrase}
            </Typography>
          </motion.div>
        </AnimatePresence>
      </Box>
      <Box
        sx={{
          mt: 1.25,
          height: 4,
          borderRadius: 1,
          background: (t) =>
            `linear-gradient(90deg, ${alpha(t.palette.primary.main, 0.08)} 0%, ${alpha(t.palette.primary.main, 0.28)} 50%, ${alpha(t.palette.primary.main, 0.08)} 100%)`,
          backgroundSize: "200% 100%",
          animation: `${shimmer} 1.8s ease-in-out infinite`,
        }}
      />
    </Paper>
  );
}

function StreamActivityPanel({
  streamProgress,
  idlePhrase,
  showIdleStrip,
  standalone,
}: {
  streamProgress: StreamProgressState;
  idlePhrase: string;
  showIdleStrip: boolean;
  standalone: boolean;
}): React.ReactElement | null {
  const hasActivity =
    streamProgress.statusText.trim() !== "" ||
    streamProgress.hypotheses.length > 0 ||
    streamProgress.toolLabel != null;

  if (showIdleStrip && !hasActivity && !streamProgress.provisionalAnswer) {
    return <IdleWaitStrip phrase={idlePhrase} />;
  }

  if (!hasActivity) return null;

  return (
    <Paper
      variant="outlined"
      sx={{
        alignSelf: "flex-start",
        maxWidth: standalone ? "100%" : "92%",
        p: 1.5,
        borderRadius: 2,
        borderLeft: 3,
        borderLeftColor: "primary.main",
        bgcolor: (t) => alpha(t.palette.primary.main, standalone ? 0.05 : 0.08),
      }}
    >
      <Typography variant="caption" fontWeight={700} color="primary.dark" sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
        <AutoAwesomeOutlinedIcon sx={{ fontSize: 14 }} />
        Working on it
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <AnimatePresence initial={false}>
          {streamProgress.statusText.trim() ? (
            <motion.div
              key="status"
              layout
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
            >
              <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                <LightbulbOutlinedIcon sx={{ fontSize: 18, color: "text.secondary", mt: 0.15 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <AssistantMarkdown
                    content={streamProgress.statusText}
                    sx={
                      standalone
                        ? { ...STANDALONE_MARKDOWN_SX, "& p": { color: "text.primary", margin: 0 } }
                        : { "& p": { color: "text.primary", margin: 0 } }
                    }
                  />
                </Box>
              </Box>
            </motion.div>
          ) : null}
        </AnimatePresence>
        {streamProgress.hypotheses.length > 0 ? (
          <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
              <FactCheckOutlinedIcon sx={{ fontSize: 18, color: "text.secondary", mt: 0.15 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                  Checking
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2, py: 0 }}>
                  {streamProgress.hypotheses.map((h, i) => (
                    <Typography key={i} component="li" variant="body2" color="text.primary" sx={{ mb: 0.25 }}>
                      {h}
                    </Typography>
                  ))}
                </Box>
              </Box>
            </Box>
          </motion.div>
        ) : null}
        <AnimatePresence mode="wait">
          {streamProgress.toolLabel ? (
            <motion.div
              key={streamProgress.toolLabel}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <Chip
                size="small"
                icon={<StorageOutlinedIcon sx={{ "&&": { fontSize: 16 } }} />}
                label={`${streamProgress.toolLabel}…`}
                variant="outlined"
                sx={{ borderColor: "primary.light", bgcolor: (t) => alpha(t.palette.primary.main, 0.06) }}
              />
            </motion.div>
          ) : null}
        </AnimatePresence>
      </Box>
    </Paper>
  );
}

export type PortfolioDataChatPanelVariant = "fab-overlay" | "standalone-page";

export interface PortfolioDataChatPanelProps {
  /** `fab-overlay`: FAB + portaled modal (legacy). `standalone-page`: full-height inline layout for `/ai-chatbot`. */
  variant?: PortfolioDataChatPanelVariant;
}

export const PortfolioDataChatPanel: React.FC<PortfolioDataChatPanelProps> = ({
  variant = "fab-overlay",
}) => {
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("md"));
  const isStandalone = variant === "standalone-page";
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<PortfolioChatMessage[]>(() =>
    typeof localStorage !== "undefined" ? parseStoredSession(localStorage.getItem(STORAGE_KEY)) : []
  );
  const [activeChartsMessageId, setActiveChartsMessageId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [streamProgress, setStreamProgress] = useState<StreamProgressState | null>(null);
  const [chartsPanelWidth, setChartsPanelWidth] = useState(readChartsPanelWidth);
  const [isResizingChartsPanel, setIsResizingChartsPanel] = useState(false);
  const [idlePhraseIndex, setIdlePhraseIndex] = useState(0);
  const [promptsCollapsed, setPromptsCollapsed] = useState(() => {
    return typeof localStorage !== "undefined"
      ? parseStoredSession(localStorage.getItem(STORAGE_KEY)).length > 0
      : false;
  });
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  // Conversation persistence (standalone-page only)
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [apiAvailable, setApiAvailable] = useState(false);
  const [convPanelOpen, setConvPanelOpen] = useState(true);
  const [loadingConversations, setLoadingConversations] = useState(false);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const standaloneLayoutRef = useRef<HTMLDivElement | null>(null);
  const fabLayoutRef = useRef<HTMLDivElement | null>(null);
  const chartsPanelWidthRef = useRef(chartsPanelWidth);
  chartsPanelWidthRef.current = chartsPanelWidth;

  const handleChartsPanelResizeStart = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = chartsPanelWidthRef.current;
    const target = e.currentTarget;
    target.setPointerCapture(e.pointerId);
    setIsResizingChartsPanel(true);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    const onMove = (ev: PointerEvent) => {
      const containerWidth =
        standaloneLayoutRef.current?.clientWidth ?? fabLayoutRef.current?.clientWidth;
      const delta = startX - ev.clientX;
      const next = clampChartsPanelWidth(startWidth + delta, containerWidth);
      chartsPanelWidthRef.current = next;
      setChartsPanelWidth(next);
    };

    const onUp = (ev: PointerEvent) => {
      target.releasePointerCapture(ev.pointerId);
      setIsResizingChartsPanel(false);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      target.removeEventListener("pointermove", onMove);
      target.removeEventListener("pointerup", onUp);
      target.removeEventListener("pointercancel", onUp);
      try {
        localStorage.setItem(CHARTS_PANEL_WIDTH_STORAGE_KEY, String(chartsPanelWidthRef.current));
      } catch {
        /* quota */
      }
    };

    target.addEventListener("pointermove", onMove);
    target.addEventListener("pointerup", onUp);
    target.addEventListener("pointercancel", onUp);
  }, []);

  useEffect(() => {
    return () => {
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, []);

  const closeChartsPanel = useCallback(() => {
    setActiveChartsMessageId(null);
  }, []);

  const toggleChartsForMessage = useCallback((messageId: string) => {
    setActiveChartsMessageId((prev) => (prev === messageId ? null : messageId));
  }, []);

  const chartsPanelOpen = activeChartsMessageId != null;
  const activeChartsMessage =
    activeChartsMessageId != null
      ? messages.find((m) => m.id === activeChartsMessageId) ?? null
      : null;
  const activeChartsUserQuestion =
    activeChartsMessageId != null
      ? findPrecedingUserQuestion(messages, activeChartsMessageId)
      : null;

  useEffect(() => {
    if (isStandalone || !open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isStandalone, open]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ messages }));
    } catch {
      /* quota */
    }
  }, [messages]);

  useEffect(() => {
    if (activeChartsMessageId == null) return;
    const stillExists = messages.some(
      (m) => m.id === activeChartsMessageId && (m.vizIntents?.length ?? 0) > 0
    );
    if (!stillExists) setActiveChartsMessageId(null);
  }, [messages, activeChartsMessageId]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages, streamProgress, loading]);

  useEffect(() => {
    if (!streamProgress) return;
    const idle =
      streamProgress.provisionalAnswer === "" &&
      streamProgress.statusText.trim() === "" &&
      streamProgress.hypotheses.length === 0 &&
      streamProgress.toolLabel == null;
    if (!idle) return;
    const id = window.setInterval(() => setIdlePhraseIndex((n) => (n + 1) % IDLE_PHRASES.length), 2200);
    return () => window.clearInterval(id);
  }, [streamProgress]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      setShowScrollToBottom(el.scrollHeight - el.scrollTop - el.clientHeight > 200);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const handleScrollToBottom = useCallback(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, []);

  // ── Conversation persistence — standalone-page only ──────────────────────

  /** Map a StoredMessage from the API back to the internal PortfolioChatMessage shape. */
  const storedToInternal = useCallback(
    (m: { role: string; text: string; api_history_content?: string | null; viz_intents?: unknown; last_tool_sql?: string | null; last_tool_row_count?: number | null; is_error?: boolean }): PortfolioChatMessage => ({
      id: newId(),
      role: m.role as "user" | "assistant",
      text: m.text,
      apiHistoryContent: m.api_history_content ?? undefined,
      vizIntents: Array.isArray(m.viz_intents) ? parseVizIntentsFromRaw(m.viz_intents) : undefined,
      lastToolSql: m.last_tool_sql ?? undefined,
      lastToolRowCount: m.last_tool_row_count ?? undefined,
      isError: m.is_error ?? undefined,
    }),
    []
  );

  /** Load conversations from the API on mount (standalone only). Falls back to localStorage on failure. */
  useEffect(() => {
    if (!isStandalone) return;
    let cancelled = false;
    const load = async () => {
      setLoadingConversations(true);
      const result = await listConversations();
      if (cancelled) return;
      if (result === null) {
        // API unreachable — keep apiAvailable=false, localStorage path stays active
        setApiAvailable(false);
      } else {
        setApiAvailable(true);
        setConversations(result);
        if (result.length > 0) {
          const conv = await getConversation(result[0].id);
          if (!cancelled && conv) {
            setActiveConversationId(conv.id);
            setMessages(conv.messages.map(storedToInternal));
            setPromptsCollapsed(conv.messages.length > 0);
          }
        }
      }
      if (!cancelled) setLoadingConversations(false);
    };
    void load();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStandalone]);

  const handleNewConversation = useCallback(async () => {
    if (loading) return;
    setMessages([]);
    setActiveChartsMessageId(null);
    setStreamProgress(null);
    setPromptsCollapsed(false);
    if (apiAvailable) {
      const conv = await createConversation("New conversation");
      if (conv) {
        setActiveConversationId(conv.id);
        setConversations((prev) => [
          { id: conv.id, title: conv.title, updated_at: conv.updated_at, preview: "" },
          ...prev,
        ]);
      }
    } else {
      setActiveConversationId(null);
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
    }
  }, [loading, apiAvailable]);

  const handleSwitchConversation = useCallback(async (id: string) => {
    if (loading) return;
    const conv = await getConversation(id);
    if (!conv) return;
    setActiveConversationId(conv.id);
    setMessages(conv.messages.map(storedToInternal));
    setPromptsCollapsed(conv.messages.length > 0);
    setActiveChartsMessageId(null);
    setStreamProgress(null);
  }, [loading, storedToInternal]);

  const handleDeleteConversation = useCallback(async (id: string) => {
    const ok = await deleteConversation(id);
    if (!ok) return;
    setConversations((prev) => {
      const remaining = prev.filter((c) => c.id !== id);
      if (id === activeConversationId) {
        if (remaining.length > 0) {
          void handleSwitchConversation(remaining[0].id);
        } else {
          setActiveConversationId(null);
          setMessages([]);
          setPromptsCollapsed(false);
        }
      }
      return remaining;
    });
  }, [activeConversationId, handleSwitchConversation]);

  const handleClose = useCallback(() => {
    if (loading) return;
    setOpen(false);
  }, [loading]);

  const handleClearThread = useCallback(() => {
    if (loading) return;
    setMessages([]);
    setActiveChartsMessageId(null);
    setStreamProgress(null);
    setActiveConversationId(null);
    setPromptsCollapsed(false);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* noop */
    }
  }, [loading]);

  const handleSend = useCallback(async () => {
    const q = input.trim();
    if (!q || loading || !AI_CONFIGURED) return;

    const chat_history = priorMessagesToChatHistory(messages);
    setInput("");
    if (!promptsCollapsed) setPromptsCollapsed(true);
    setMessages((prev) => [...prev, { id: newId(), role: "user", text: q, timestamp: Date.now() }]);
    setLoading(true);
    setStreamProgress({
      statusText: "",
      hypotheses: [],
      toolLabel: null,
      provisionalAnswer: "",
    });

    let accumulatedAnswer = "";
    let receivedDone = false;

    try {
      for await (const ev of postMultiChatbotStream({ userQuestion: q, chatHistory: chat_history })) {
        if (ev.type === "status") {
          setStreamProgress((p) => (p ? { ...p, statusText: ev.text } : p));
        } else if (ev.type === "hypotheses") {
          setStreamProgress((p) => (p ? { ...p, hypotheses: [...ev.items] } : p));
        } else if (ev.type === "tool_start") {
          setStreamProgress((p) => (p ? { ...p, toolLabel: ev.label } : p));
        } else if (ev.type === "tool_end") {
          setStreamProgress((p) => (p ? { ...p, toolLabel: null } : p));
        } else if (ev.type === "answer_token") {
          accumulatedAnswer += ev.text;
          setStreamProgress((p) => (p ? { ...p, provisionalAnswer: accumulatedAnswer } : p));
        } else if (ev.type === "error") {
          throw new Error(ev.message);
        } else if (ev.type === "done") {
          receivedDone = true;
          const finalAnswer = accumulatedAnswer.trim() || "(No answer produced.)";
          const rawViz = Array.isArray(ev.viz_intents) ? ev.viz_intents : [];
          const vizIntents = parseVizIntentsFromRaw(rawViz);
          const apiHistoryContent = truncateForHistory(finalAnswer, ASSISTANT_HISTORY_MAX_CHARS);
          const assistantMsg: PortfolioChatMessage = {
            id: newId(),
            role: "assistant",
            text: finalAnswer,
            apiHistoryContent,
            vizIntents: vizIntents.length > 0 ? vizIntents : undefined,
            lastToolSql: ev.last_tool_sql ?? null,
            lastToolRowCount: ev.last_tool_row_count ?? null,
            timestamp: Date.now(),
          };
          setMessages((prev) => [...prev, assistantMsg]);

          // Persist to API (standalone only, silently falls back to localStorage on failure)
          if (isStandalone && apiAvailable) {
            const userMsg: PortfolioChatMessage = { id: newId(), role: "user", text: q, timestamp: Date.now() };
            let convId = activeConversationId;
            if (!convId) {
              const newConv = await createConversation("New conversation");
              if (newConv) {
                convId = newConv.id;
                setActiveConversationId(convId);
                setConversations((prev) => [
                  { id: newConv.id, title: newConv.title, updated_at: newConv.updated_at, preview: q.slice(0, 80) },
                  ...prev,
                ]);
              }
            }
            if (convId) {
              // Auto-title on first message
              setConversations((prev) =>
                prev.map((c) =>
                  c.id === convId && c.title === "New conversation"
                    ? { ...c, title: q.slice(0, 60), preview: q.slice(0, 80) }
                    : c
                )
              );
              if (conversations.find((c) => c.id === convId)?.title === "New conversation") {
                void renameConversation(convId, q.slice(0, 60));
              }
              const saved = await apiAppendMessages(convId, [
                { role: userMsg.role, text: userMsg.text, api_history_content: userMsg.apiHistoryContent ?? null },
                {
                  role: assistantMsg.role,
                  text: assistantMsg.text,
                  api_history_content: assistantMsg.apiHistoryContent ?? null,
                  viz_intents: assistantMsg.vizIntents ?? null,
                  last_tool_sql: assistantMsg.lastToolSql ?? null,
                  last_tool_row_count: assistantMsg.lastToolRowCount ?? null,
                },
              ]);
              if (!saved) {
                // API failed mid-session — fall back to localStorage
                try {
                  localStorage.setItem(STORAGE_KEY, JSON.stringify({ messages: [...messages, userMsg, assistantMsg] }));
                } catch { /* quota */ }
              }
            }
          }
        }
      }

      if (!receivedDone) {
        if (accumulatedAnswer.trim()) {
          const finalAnswer = accumulatedAnswer.trim();
          const apiHistoryContent = truncateForHistory(finalAnswer, ASSISTANT_HISTORY_MAX_CHARS);
          setMessages((prev) => [
            ...prev,
            { id: newId(), role: "assistant", text: finalAnswer, apiHistoryContent, timestamp: Date.now() },
          ]);
        } else {
          setMessages((prev) => [
            ...prev,
            {
              id: newId(),
              role: "assistant",
              text: "Stream ended without a final response.",
              isError: true,
            },
          ]);
        }
      }
    } catch (e) {
      const msg = errorMessage(e);
      setMessages((prev) => [...prev, { id: newId(), role: "assistant", text: msg, isError: true }]);
    } finally {
      setStreamProgress(null);
      setLoading(false);
    }
  }, [input, loading, messages, promptsCollapsed, isStandalone, apiAvailable, activeConversationId, conversations]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  /** Above DynamicSidebar drawer paper (z-index 1300); portaled to body to avoid main-column overflow clipping. */
  const floatingZ = (t: Theme) => t.zIndex.snackbar;

  const paperSx = {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderRadius: 2,
  } as const;

  const streamIdlePhrase = IDLE_PHRASES[idlePhraseIndex % IDLE_PHRASES.length] ?? IDLE_PHRASES[0];
  const showStreamIdleStrip =
    streamProgress != null &&
    streamProgress.provisionalAnswer === "" &&
    streamProgress.statusText.trim() === "" &&
    streamProgress.hypotheses.length === 0 &&
    streamProgress.toolLabel == null;

  const messageBubbles = (opts: { standalone: boolean }) =>
    messages.map((m, i) => {
      const vizCount = m.vizIntents?.length ?? 0;
      const showChartBox = m.role === "assistant" && !m.isError && vizCount > 0;
      const isChartsActive = activeChartsMessageId === m.id;
      const isNewTurn = i > 0 && m.role === "user" && messages[i - 1].role === "assistant";
      const showCopyBtn = opts.standalone && m.role === "assistant" && !m.isError;
      return (
        <Box
          key={m.id}
          sx={{
            alignSelf: m.role === "user" ? "flex-end" : "flex-start",
            maxWidth: opts.standalone ? "92%" : "92%",
            width: showChartBox ? "100%" : undefined,
            mt: isNewTurn && opts.standalone ? 1.5 : 0,
            ...(showCopyBtn && {
              position: "relative",
              "& .msg-copy-btn": { opacity: 0, transition: "opacity 0.15s" },
              "&:hover .msg-copy-btn": { opacity: 1 },
            }),
          }}
        >
          <Box
            sx={{
              px: opts.standalone ? 2 : 1.5,
              py: opts.standalone ? 1.25 : 1,
              borderRadius: opts.standalone ? 3 : 2,
              bgcolor:
                m.role === "user"
                  ? opts.standalone
                    ? alpha(SEARCE_COLORS.primary, 0.12)
                    : SEARCE_COLORS.primary_lightest
                  : opts.standalone
                    ? "background.paper"
                    : alpha(SEARCE_COLORS.primary, 0.08),
              border: opts.standalone ? "1px solid" : 1,
              borderColor: m.isError ? "error.light" : opts.standalone ? "divider" : "divider",
              boxShadow: opts.standalone && m.role === "assistant" && !m.isError ? "0 1px 2px rgba(0,0,0,0.04)" : "none",
            }}
          >
            {m.role === "assistant" && !m.isError ? (
              <AssistantMarkdown content={m.text} sx={opts.standalone ? STANDALONE_MARKDOWN_SX : undefined} />
            ) : (
              <Typography
                variant={opts.standalone ? "body1" : "body2"}
                sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
              >
                {m.text}
              </Typography>
            )}
          </Box>
          {opts.standalone && m.timestamp ? (
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{
                display: "block",
                textAlign: m.role === "user" ? "right" : "left",
                mt: 0.25,
                fontSize: "0.6875rem",
              }}
            >
              {new Intl.DateTimeFormat("en", { hour: "numeric", minute: "2-digit" }).format(
                new Date(m.timestamp)
              )}
            </Typography>
          ) : null}
          {showCopyBtn ? (
            <Tooltip title={copiedMessageId === m.id ? "Copied!" : "Copy"} placement="right">
              <IconButton
                className="msg-copy-btn"
                size="small"
                onClick={() => {
                  void navigator.clipboard.writeText(m.text).then(() => {
                    setCopiedMessageId(m.id);
                    window.setTimeout(() => setCopiedMessageId(null), 2000);
                  });
                }}
                sx={{
                  position: "absolute",
                  top: 4,
                  right: { xs: 4, sm: -36 },
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                  p: 0.5,
                  "&:hover": { bgcolor: "grey.50" },
                }}
              >
                {copiedMessageId === m.id ? (
                  <DoneIcon sx={{ fontSize: 14, color: "success.main" }} />
                ) : (
                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                )}
              </IconButton>
            </Tooltip>
          ) : null}
          {showChartBox ? (
            <ChartOpenBox
              count={vizCount}
              isActive={isChartsActive}
              onClick={() => toggleChartsForMessage(m.id)}
              standalone={opts.standalone}
            />
          ) : null}
        </Box>
      );
    });

  const streamProvisionalBlock = (standalone: boolean) =>
    streamProgress?.provisionalAnswer ? (
      <Box
        sx={{
          alignSelf: "flex-start",
          maxWidth: standalone ? "100%" : "92%",
          px: standalone ? 2 : 1.5,
          py: standalone ? 1.25 : 1,
          borderRadius: standalone ? 3 : 2,
          bgcolor: standalone ? "background.paper" : alpha(SEARCE_COLORS.primary, 0.08),
          border: standalone ? "1px solid" : 1,
          borderColor: "divider",
          boxShadow: standalone ? "0 1px 2px rgba(0,0,0,0.04)" : "none",
        }}
      >
        <Typography
          variant={standalone ? "body2" : "caption"}
          fontWeight={600}
          color="text.secondary"
          sx={{ display: "block", mb: 0.5 }}
        >
          Assistant
        </Typography>
        <Box sx={{ wordBreak: "break-word" }}>
          <AssistantMarkdown content={streamProgress.provisionalAnswer} sx={standalone ? STANDALONE_MARKDOWN_SX : undefined} />
          <Box
            component="span"
            sx={{
              display: "inline-block",
              width: "0.45em",
              height: "1em",
              bgcolor: "text.secondary",
              ml: "1px",
              verticalAlign: "text-bottom",
              animation: `${streamCursorBlink} 1s steps(1, end) infinite`,
            }}
          />
        </Box>
      </Box>
    ) : null;

  const renderStreamSection = (standalone: boolean) =>
    streamProgress != null ? (
      <>
        <StreamActivityPanel
          streamProgress={streamProgress}
          idlePhrase={streamIdlePhrase}
          showIdleStrip={Boolean(showStreamIdleStrip)}
          standalone={standalone}
        />
        {streamProvisionalBlock(standalone)}
      </>
    ) : null;

  const chartsBody = (
    <TurnChartsBody activeMessage={activeChartsMessage} userQuestionPreview={activeChartsUserQuestion} />
  );

  const renderChartsResizeHandle = () => (
    <Tooltip title="Drag to resize · double-click to reset" placement="left">
      <Box
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize charts panel"
        onPointerDown={handleChartsPanelResizeStart}
        onDoubleClick={() => {
          const next = CHARTS_PANEL_DEFAULT_WIDTH;
          chartsPanelWidthRef.current = next;
          setChartsPanelWidth(next);
          try {
            localStorage.setItem(CHARTS_PANEL_WIDTH_STORAGE_KEY, String(next));
          } catch {
            /* quota */
          }
        }}
        sx={{
          width: 8,
          flexShrink: 0,
          cursor: "col-resize",
          touchAction: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: isResizingChartsPanel ? alpha(SEARCE_COLORS.primary, 0.12) : "transparent",
          transition: isResizingChartsPanel ? "none" : "background-color 0.15s",
          "&:hover": { bgcolor: alpha(SEARCE_COLORS.primary, 0.08) },
        }}
      >
        <Box
          sx={{
            width: 3,
            height: 40,
            borderRadius: 1.5,
            bgcolor: isResizingChartsPanel ? "primary.main" : "divider",
          }}
        />
      </Box>
    </Tooltip>
  );

  const renderChartsSideColumn = () =>
    !isNarrow ? (
      <Box
        sx={{
          width: chartsPanelOpen ? chartsPanelWidth : 0,
          flexShrink: 0,
          transition: isResizingChartsPanel
            ? "none"
            : theme.transitions.create("width", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.shorter,
              }),
          overflow: "hidden",
          borderLeft: chartsPanelOpen ? `1px solid ${theme.palette.divider}` : "none",
          bgcolor: "background.paper",
          borderRadius: chartsPanelOpen ? "0 12px 12px 0" : 0,
        }}
      >
        <Box
          sx={{
            width: chartsPanelWidth,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            boxSizing: "border-box",
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.25,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              flexShrink: 0,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <BarChartIcon fontSize="small" color="primary" />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Charts
              </Typography>
              {(activeChartsMessage?.vizIntents?.length ?? 0) > 0 && (
                <Chip
                  label={activeChartsMessage!.vizIntents!.length}
                  size="small"
                  color="primary"
                  sx={{ height: 18, fontSize: "0.6875rem", "& .MuiChip-label": { px: 0.75 } }}
                />
              )}
            </Box>
            <IconButton aria-label="Close charts" onClick={closeChartsPanel} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>{chartsBody}</Box>
        </Box>
      </Box>
    ) : null;

  const renderChartsMobileDrawer = (title: string) => (
    <Drawer
      anchor="right"
      open={isNarrow && chartsPanelOpen}
      onClose={closeChartsPanel}
      PaperProps={{
        sx: {
          width: `min(calc(100vw - 24px), ${clampChartsPanelWidth(chartsPanelWidth)}px)`,
          p: 0,
        },
      }}
    >
      <Box sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: 1, borderColor: "divider" }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <IconButton aria-label="Close charts" onClick={closeChartsPanel} size="small">
          <CloseIcon />
        </IconButton>
      </Box>
      <Box sx={{ p: 2, overflow: "auto" }}>{chartsBody}</Box>
    </Drawer>
  );

  // ── Conversations sidebar helpers ────────────────────────────────────────

  const formatRelativeTime = (iso: string): string => {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60_000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  const conversationsSidebar = (
    <Box
      sx={{
        width: 240,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        borderRight: `1px solid ${theme.palette.divider}`,
        bgcolor: "background.paper",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${theme.palette.divider}`,
          flexShrink: 0,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Conversations
        </Typography>
        <Tooltip title="New conversation">
          <IconButton size="small" onClick={() => void handleNewConversation()} disabled={loading}>
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* List */}
      <Box sx={{ flex: 1, overflow: "auto" }}>
        {loadingConversations ? (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 3 }}>
            <CircularProgress size={20} />
          </Box>
        ) : conversations.length === 0 ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", p: 2 }}>
            No conversations yet.
          </Typography>
        ) : (
          <List dense disablePadding>
            {conversations.map((conv) => (
              <ListItemButton
                key={conv.id}
                selected={conv.id === activeConversationId}
                onClick={() => void handleSwitchConversation(conv.id)}
                sx={{
                  py: 0.75,
                  px: 1.5,
                  alignItems: "flex-start",
                  "& .conv-delete": { opacity: 0, transition: "opacity 0.15s" },
                  "&:hover .conv-delete": { opacity: 1 },
                }}
              >
                <ListItemText
                  primary={conv.title}
                  secondary={formatRelativeTime(conv.updated_at)}
                  primaryTypographyProps={{ noWrap: true, variant: "body2", sx: { fontWeight: conv.id === activeConversationId ? 600 : 400 } }}
                  secondaryTypographyProps={{ variant: "caption" }}
                  sx={{ mr: 0.5 }}
                />
                <Tooltip title="Delete" placement="right">
                  <IconButton
                    className="conv-delete"
                    size="small"
                    onClick={(e) => { e.stopPropagation(); void handleDeleteConversation(conv.id); }}
                    sx={{ mt: 0.25, flexShrink: 0, color: "text.secondary" }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </ListItemButton>
            ))}
          </List>
        )}
      </Box>
    </Box>
  );

  const standalonePromptsBar = (
    <Collapse in={!promptsCollapsed} unmountOnExit>
      <Box
        sx={{
          px: { xs: 1.5, sm: 2 },
          pt: 1,
          pb: 0.5,
          maxWidth: STANDALONE_CHAT_MAX_WIDTH,
          mx: "auto",
          width: "100%",
        }}
      >
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, alignItems: "center" }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, fontWeight: 500, flexShrink: 0 }}>
            Try:
          </Typography>
          {STANDALONE_STARTER_PROMPTS.map((prompt) => (
            <Chip
              key={prompt}
              label={prompt}
              size="small"
              variant="outlined"
              onClick={() => {
                setInput(prompt);
                setTimeout(() => inputRef.current?.focus(), 0);
              }}
              sx={{
                height: "auto",
                py: 0.5,
                fontSize: "0.75rem",
                "& .MuiChip-label": { whiteSpace: "normal", textAlign: "left" },
                "&:hover": {
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
                  borderColor: "primary.main",
                },
              }}
            />
          ))}
        </Box>
      </Box>
    </Collapse>
  );

  const standaloneComposer = (
    <Box
      sx={{
        flexShrink: 0,
        px: { xs: 1.5, sm: 2 },
        pb: 2,
        pt: 0,
        bgcolor: "background.default",
        borderTop: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box sx={{ maxWidth: STANDALONE_CHAT_MAX_WIDTH, mx: "auto", width: "100%" }}>
        <Paper
          elevation={0}
          sx={{
            display: "flex",
            alignItems: "flex-end",
            gap: 1,
            p: "6px 8px 6px 14px",
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: "background.paper",
            boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
          }}
        >
          <TextField
            inputRef={inputRef}
            fullWidth
            multiline
            maxRows={6}
            variant="standard"
            placeholder="Ask anything about your portfolio…"
            value={input}
            disabled={loading || !AI_CONFIGURED}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            InputProps={{
              disableUnderline: true,
              sx: { fontSize: "0.9375rem", py: 0.5 },
            }}
          />
          <Tooltip title={loading ? "Sending…" : "Send"}>
            <span>
              <IconButton
                color="primary"
                onClick={() => void handleSend()}
                disabled={loading || !input.trim() || !AI_CONFIGURED}
                aria-label={loading ? "Sending" : "Send"}
                aria-busy={loading}
                sx={{ flexShrink: 0 }}
              >
                {loading ? <CircularProgress color="inherit" size={22} thickness={5} /> : <SendIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Paper>
      </Box>
    </Box>
  );

  const fabComposer = (
    <Box sx={{ p: 1.5, pt: 0, display: "flex", gap: 1, alignItems: "flex-end", flexShrink: 0, borderTop: 1, borderColor: "divider" }}>
      <TextField
        fullWidth
        multiline
        maxRows={4}
        size="small"
        placeholder="Your question…"
        value={input}
        disabled={loading || !AI_CONFIGURED}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
      />
      <Tooltip title={loading ? "Sending…" : "Send"}>
        <span>
          <IconButton
            color="primary"
            onClick={() => void handleSend()}
            disabled={loading || !input.trim() || !AI_CONFIGURED}
            aria-label={loading ? "Sending" : "Send"}
            aria-busy={loading}
          >
            {loading ? <CircularProgress color="inherit" size={22} thickness={5} /> : <SendIcon />}
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );

  const standaloneEmptyState = messages.length === 0 && (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        py: 6,
        px: 2,
        gap: 1,
      }}
    >
      <AutoAwesomeOutlinedIcon sx={{ fontSize: 40, color: "primary.main", opacity: 0.7, mb: 0.5 }} />
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        Ask me Anything AI
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, mx: "auto" }}>
        Natural-language answers, charts, and tables grounded in projects you can access.
        Start with a suggestion below or type your own question.
      </Typography>
    </Box>
  );

  if (isStandalone) {
    return (
      <Box
        sx={{
          height: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.default",
          boxSizing: "border-box",
          p: { xs: 1, sm: 2 },
          ...STANDALONE_UI_SX,
        }}
      >
        <Box ref={standaloneLayoutRef} sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "row", overflow: "hidden", gap: 0 }}>
          {/* Conversations sidebar — desktop: inline panel, mobile: Drawer */}
          {!isNarrow && convPanelOpen && conversationsSidebar}
          <Drawer
            anchor="left"
            open={isNarrow && convPanelOpen}
            onClose={() => setConvPanelOpen(false)}
            PaperProps={{ sx: { width: 260 } }}
          >
            {conversationsSidebar}
          </Drawer>
          <Paper
            component="main"
            elevation={0}
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              borderRadius: 3,
              overflow: "hidden",
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: "background.paper",
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.25,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                flexShrink: 0,
                borderBottom: `1px solid ${theme.palette.divider}`,
                bgcolor: "background.paper",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Tooltip title={convPanelOpen ? "Hide conversations" : "Show conversations"}>
                  <IconButton size="small" onClick={() => setConvPanelOpen((p) => !p)}>
                    {convPanelOpen ? <ChevronLeftIcon fontSize="small" /> : <ForumOutlinedIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <Typography id="portfolio-data-chat-title" variant="h6" sx={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 1 }}>
                  <InsightsIcon color="primary" />
                  Ask me Anything AI
                </Typography>
              </Box>
              <Button
                size="medium"
                onClick={handleClearThread}
                disabled={loading || messages.length === 0}
                sx={{ textTransform: "none", minWidth: 0 }}
              >
                Clear
              </Button>
            </Box>

            {!AI_CONFIGURED && (
              <Alert severity="warning" sx={{ mx: 2, mt: 2, mb: 0 }}>
                Set <code>REACT_APP_AI_CHATBOT_URL</code> in your environment (see <code>.env.example</code>).
              </Alert>
            )}

            <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", position: "relative" }}>
              <Box
                ref={scrollRef}
                sx={{
                  flex: 1,
                  overflow: "auto",
                  px: { xs: 1.5, sm: 2 },
                  py: 2,
                }}
              >
                <Box
                  sx={{
                    maxWidth: STANDALONE_CHAT_MAX_WIDTH,
                    mx: "auto",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  {standaloneEmptyState}
                  {messageBubbles({ standalone: true })}
                  {renderStreamSection(true)}
                </Box>
              </Box>
              {showScrollToBottom && (
                <Tooltip title="Scroll to latest" placement="left">
                  <IconButton
                    onClick={handleScrollToBottom}
                    size="small"
                    sx={{
                      position: "absolute",
                      bottom: 72,
                      right: 16,
                      zIndex: 10,
                      bgcolor: "background.paper",
                      border: "1px solid",
                      borderColor: "divider",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
                      "&:hover": { bgcolor: "grey.100" },
                    }}
                  >
                    <KeyboardArrowDownIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Box sx={{ flexShrink: 0, display: "flex", flexDirection: "column" }}>
                {standalonePromptsBar}
                {standaloneComposer}
              </Box>
            </Box>
          </Paper>

          {!isNarrow && chartsPanelOpen && renderChartsResizeHandle()}
          {renderChartsSideColumn()}
        </Box>

        {renderChartsMobileDrawer("Charts")}
      </Box>
    );
  }

  const chatPaper = (
    <Paper elevation={12} sx={{ ...paperSx, flex: 1, minHeight: 0 }}>
      <Box
        sx={{
          px: 2,
          py: 1.25,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          flexShrink: 0,
          bgcolor: SEARCE_COLORS.primary,
          color: SEARCE_COLORS.on_primary,
        }}
      >
        <Typography id="portfolio-data-chat-title" variant="subtitle1" sx={{ fontWeight: 600 }}>
          Portfolio data chat
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Button
            size="small"
            onClick={handleClearThread}
            disabled={loading || messages.length === 0}
            sx={{ color: "inherit", textTransform: "none", fontSize: "0.75rem", minWidth: 0 }}
          >
            Clear thread
          </Button>
          <IconButton size="small" onClick={handleClose} disabled={loading} sx={{ color: "inherit" }} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {!AI_CONFIGURED && (
        <Alert severity="warning" sx={{ m: 2, mb: 0 }}>
          Set <code>REACT_APP_AI_CHATBOT_URL</code> in your environment (see <code>.env.example</code>).
        </Alert>
      )}

      <Box ref={fabLayoutRef} sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "row", overflow: "hidden" }}>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            bgcolor: alpha(SEARCE_COLORS.primary, 0.04),
          }}
        >
          <Box
            ref={scrollRef}
            sx={{
              flex: 1,
              overflow: "auto",
              p: 2,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            {messages.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Ask questions about portfolio data. When the assistant returns charts, open them from the box under each answer.
              </Typography>
            )}
            {messageBubbles({ standalone: false })}
            {renderStreamSection(false)}
          </Box>

          {fabComposer}
        </Box>

        {!isNarrow && chartsPanelOpen && renderChartsResizeHandle()}
        {renderChartsSideColumn()}
      </Box>

      {renderChartsMobileDrawer("Charts")}
    </Paper>
  );

  const floatingLayer = (
    <>
      {open && (
        <Box
          role="dialog"
          aria-modal="true"
          aria-labelledby="portfolio-data-chat-title"
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: floatingZ,
            display: "flex",
            flexDirection: "column",
            boxSizing: "border-box",
            p: { xs: 1, sm: 2 },
            bgcolor: alpha(SEARCE_COLORS.neutral_900, 0.35),
            backdropFilter: "blur(2px)",
          }}
        >
          <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>{chatPaper}</Box>
        </Box>
      )}

      {!open && (
        <Fab
          color="secondary"
          variant="extended"
          onClick={() => setOpen(true)}
          sx={{
            position: "fixed",
            bottom: 100,
            right: 24,
            zIndex: floatingZ,
            boxShadow: 4,
            fontWeight: 700,
            letterSpacing: 0.02,
            textTransform: "uppercase",
            fontSize: "0.7rem",
            px: 2,
          }}
        >
          <InsightsIcon sx={{ mr: 1 }} />
          Portfolio data chat
        </Fab>
      )}
    </>
  );

  if (typeof document === "undefined") {
    return null;
  }
  return createPortal(floatingLayer, document.body);
};
